import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Search, Share2, ShieldCheck, X } from "lucide-react";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import { trackProgress } from "@/lib/trophies";
import VaultDetail from "@/pages/idevra/VaultDetail";
import VaultGate, { readVaultAccess } from "@/pages/idevra/VaultGate";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const rise = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

const STATUS_STYLE = {
  Granted: { dot: "●", cls: "text-[#0284C7]" },
  "Under Prosecution": { dot: "◐", cls: "text-[#D97706]" },
  "Patent Pending": { dot: "◐", cls: "text-[#D97706]" },
  "In Preparation": { dot: "◌", cls: "text-[#94A3B8]" },
  Filed: { dot: "○", cls: "text-[#94A3B8]" },
};

const TM_STATUS_STYLE = {
  Registered: { dot: "●", cls: "text-[#0284C7]" },
  Advertised: { dot: "◐", cls: "text-[#D97706]" },
  "In Review": { dot: "◌", cls: "text-[#D97706]" },
  Applied: { dot: "○", cls: "text-[#94A3B8]" },
};

const VAULT_TOTAL = IDEVRA.patents.items.length + IDEVRA.trademarks.items.length;
const seeEntry = (key) => trackProgress("idevra_vault_seen", key, VAULT_TOTAL, "archivist");

export default function VaultPanel({ focusIdx }) {
  const { office, counts, items, pipeline, note } = IDEVRA.patents;
  const tm = IDEVRA.trademarks;
  const [access, setAccess] = useState(readVaultAccess);
  const granted = access?.status === "approved";
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const searchRef = useRef(null);

  const openDetail = (rec) => {
    sfx.clank();
    setDetail(rec);
  };
  const closeDetail = () => {
    sfx.back();
    setDetail(null);
  };

  const q = query.trim().toLowerCase();
  const fPatents = items
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => !q || `${p.family} ${p.kind} ${p.number} ${p.status}`.toLowerCase().includes(q));
  const fTms = tm.items
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !q || `${m.mark} ${m.type} ${m.number} ${m.goods} ${m.status}`.toLowerCase().includes(q));
  const secretQ = q === "1986" || q === "konami";
  const empty = q && !secretQ && fPatents.length === 0 && fTms.length === 0;

  useEffect(() => {
    if (secretQ) sfx.whoosh();
  }, [secretQ]);

  useEffect(() => {
    if (!granted) return undefined;
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
        sfx.tick();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [granted]);

  useEffect(() => {
    const onEnter = (e) => {
      if (e.key !== "Enter" || detail || !granted) return;
      if (["INPUT", "TEXTAREA", "BUTTON"].includes(e.target.tagName)) return;
      if (focusIdx < items.length) openDetail({ type: "patent", item: items[focusIdx] });
      else if (focusIdx - items.length < tm.items.length)
        openDetail({ type: "tm", item: tm.items[focusIdx - items.length] });
    };
    window.addEventListener("keydown", onEnter);
    return () => window.removeEventListener("keydown", onEnter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIdx, detail]);

  useEffect(() => {
    if (!granted) return;
    if (focusIdx < items.length) seeEntry(`p-${focusIdx}`);
    else if (focusIdx - items.length < tm.items.length) seeEntry(`t-${focusIdx - items.length}`);
  }, [granted, focusIdx, items.length, tm.items.length]);

  const share = async () => {
    sfx.clank();
    const url = `${window.location.origin}/api/share/vault`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      if (navigator.share) navigator.share({ title: "IDEVRA IP VAULT", url }).catch(() => {});
    }
  };
  if (!granted) {
    return <VaultGate initial={access} onGranted={setAccess} />;
  }

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-6">
      <motion.div variants={rise} className="iv-eyebrow">
        — IP VAULT · {office.toUpperCase()}
      </motion.div>
      <motion.div variants={rise} className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="f-orbitron text-xl sm:text-3xl font-bold text-[#0F172A]">
          The vault behind the ventures.
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="iv-glass flex h-10 items-center gap-2 rounded-full px-4">
            <Search size={13} className="shrink-0 text-[#94A3B8]" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setQuery("");
                  e.currentTarget.blur();
                  sfx.back();
                }
              }}
              placeholder="FILTER RECORDS… ( / )"
              data-testid="vault-search-input"
              className="f-mono w-36 sm:w-48 bg-transparent text-[10px] tracking-[0.15em] text-[#0F172A] placeholder:text-[#94A3B8] outline-none"
            />
            {query && (
              <button
                type="button"
                data-testid="vault-search-clear"
                aria-label="Clear filter"
                onClick={() => {
                  setQuery("");
                  sfx.back();
                }}
                className="text-[#94A3B8] transition-colors hover:text-[#0284C7]"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <button
            type="button"
            data-testid="vault-share-btn"
            onClick={share}
            className="iv-chip flex items-center gap-2 !py-2 transition-colors hover:text-[#0284C7]"
          >
            {copied ? <Check size={13} className="text-[#0284C7]" /> : <Share2 size={13} />}
            {copied ? "LINK COPIED" : "SHARE VAULT"}
          </button>
        </div>
      </motion.div>

      {q && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="f-mono text-[9px] tracking-[0.25em] text-[#64748B]"
          data-testid="vault-search-results"
        >
          {fPatents.length + fTms.length} RECORD{fPatents.length + fTms.length === 1 ? "" : "S"} MATCH “{query.toUpperCase()}”
        </motion.div>
      )}

      {secretQ && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="iv-glass rounded-2xl px-6 py-6"
          data-testid="vault-secret-archive"
        >
          <div className="f-mono text-[9px] tracking-[0.3em] text-[#D97706]">◐ DECLASSIFIED · OPERATOR CLEARANCE GRANTED</div>
          <div className="f-orbitron mt-2 text-sm font-bold text-[#0F172A]">ARCHIVE 1986 · OPENING SEQUENCE RECOVERED</div>
          <div className="f-mono mt-3 text-[13px] tracking-[0.45em] text-[#0284C7]">↑ ↑ ↓ ↓ █ █ █ █ █ █</div>
          <p className="mt-3 max-w-xl text-[11px] leading-relaxed text-[#64748B]">
            A second fragment sleeps at the bottom of this vault, below the trademarks. Join the two halves,
            key the full sequence anywhere on the console — and the operators of 1986 will answer.
          </p>
        </motion.div>
      )}

      {empty && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="iv-glass rounded-2xl px-6 py-8 text-center f-mono text-[10px] tracking-[0.3em] text-[#94A3B8]"
          data-testid="vault-search-empty"
        >
          NO VAULT RECORDS MATCH — CLEAR FILTER TO SEE ALL {items.length + tm.items.length} ASSETS
        </motion.div>
      )}
      {!q && (
      <motion.div variants={rise} className="flex flex-wrap gap-3" data-testid="vault-counts">
        <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">{String(counts.granted).padStart(2, "0")}</span> GRANTED</span>
        <span className="iv-chip"><span className="f-orbitron text-[#D97706]">{String(counts.prosecution).padStart(2, "0")}</span> UNDER PROSECUTION</span>
        <span className="iv-chip"><span className="f-orbitron text-[#D97706]">{String(counts.pending).padStart(2, "0")}</span> PATENT PENDING</span>
        <span className="iv-chip"><span className="f-orbitron text-[#64748B]">{String(counts.filed).padStart(2, "0")}</span> FILED</span>
        <span className="iv-chip"><span className="f-orbitron text-[#64748B]">{String(counts.preparation).padStart(2, "0")}</span> IN PREPARATION</span>
        <span className="iv-chip"><span className="f-orbitron text-[#94A3B8]">{String(counts.pipeline).padStart(2, "0")}</span> IN SPECIFICATION</span>
      </motion.div>
      )}

      {fPatents.length > 0 && (
      <motion.div variants={rise} className="iv-glass overflow-hidden rounded-2xl" data-testid="vault-list">
        {fPatents.map(({ p, i }) => {
          const s = STATUS_STYLE[p.status];
          const focused = !q && i === focusIdx;
          return (
            <div
              key={`${p.family}-${p.number}`}
              data-testid={`vault-row-${i}`}
              onMouseEnter={() => seeEntry(`p-${i}`)}
              onClick={() => openDetail({ type: "patent", item: p })}
              className={`grid cursor-pointer grid-cols-[2rem_1fr_auto] items-center gap-3 border-b border-white/60 px-4 py-3 transition-colors last:border-b-0 hover:bg-white/70 sm:grid-cols-[2.5rem_1fr_1fr_auto] sm:px-6 ${
                focused ? "bg-white/85 shadow-[inset_0_0_0_2px_rgba(56,189,248,0.5)]" : ""
              }`}
            >
              <span className="f-orbitron text-[10px] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="block f-orbitron text-[12px] font-bold text-[#0F172A]">{p.family}</span>
                <span className="f-mono text-[8px] tracking-[0.25em] text-[#64748B]">{p.kind}</span>
              </span>
              <span className="hidden f-mono text-[10px] tracking-[0.08em] text-[#475569] sm:block">{p.number}</span>
              <span className={`f-mono text-[9px] tracking-[0.18em] ${s.cls}`}>
                {s.dot} {p.status.toUpperCase()}
              </span>
            </div>
          );
        })}
      </motion.div>
      )}

      {!q && (
      <>
      <motion.div variants={rise} className="flex flex-wrap items-center gap-3" data-testid="vault-pipeline">
        <span className="flex items-center gap-2 f-mono text-[9px] tracking-[0.25em] text-[#64748B]">
          <ShieldCheck size={13} color="#0284C7" /> IN SPECIFICATION &amp; FILING:
        </span>
        {pipeline.map((t) => (
          <span key={t} className="iv-chip !py-1.5 !text-[9px]">{t}</span>
        ))}
      </motion.div>

      <motion.p variants={rise} className="max-w-3xl text-[13px] italic leading-relaxed text-[#64748B]">
        {note}
      </motion.p>
      </>
      )}

      {/* Trademarks */}
      {(!q || fTms.length > 0) && (
      <motion.div variants={rise} className="iv-eyebrow pt-4" data-testid="tm-eyebrow">
        — TRADEMARKS · {tm.office.toUpperCase()}
      </motion.div>
      )}
      {!q && (
      <motion.div variants={rise} className="flex flex-wrap gap-3" data-testid="tm-counts">
        <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">{String(tm.counts.registered).padStart(2, "0")}</span> REGISTERED</span>
        <span className="iv-chip"><span className="f-orbitron text-[#D97706]">{String(tm.counts.advertised).padStart(2, "0")}</span> ADVERTISED</span>
        <span className="iv-chip"><span className="f-orbitron text-[#D97706]">{String(tm.counts.inReview).padStart(2, "0")}</span> IN REVIEW</span>
        <span className="iv-chip"><span className="f-orbitron text-[#64748B]">{String(tm.counts.applied).padStart(2, "0")}</span> APPLIED</span>
      </motion.div>
      )}

      {fTms.length > 0 && (
      <motion.div variants={rise} className="iv-glass overflow-hidden rounded-2xl" data-testid="tm-list">
        {fTms.map(({ m, i }) => {
          const s = TM_STATUS_STYLE[m.status];
          const focused = !q && focusIdx === items.length + i;
          return (
            <div
              key={m.number}
              data-testid={`tm-row-${i}`}
              onMouseEnter={() => seeEntry(`t-${i}`)}
              onClick={() => openDetail({ type: "tm", item: m })}
              className={`grid cursor-pointer grid-cols-[2rem_1fr_auto] items-center gap-3 border-b border-white/60 px-4 py-3 transition-colors last:border-b-0 hover:bg-white/70 sm:grid-cols-[2.5rem_1fr_1fr_auto] sm:px-6 ${
                focused ? "bg-white/85 shadow-[inset_0_0_0_2px_rgba(56,189,248,0.5)]" : ""
              }`}
            >
              <span className="f-orbitron text-[10px] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="block f-orbitron text-[12px] font-bold text-[#0F172A]">
                  {m.mark} <span className="font-normal text-[#94A3B8]">™</span>
                </span>
                <span className="f-mono text-[8px] tracking-[0.2em] text-[#64748B]">
                  {m.type} · CLASS {m.cls}
                </span>
                <span className="block truncate text-[10px] text-[#94A3B8]">{m.goods}</span>
              </span>
              <span className="hidden f-mono text-[10px] tracking-[0.08em] text-[#475569] sm:block">
                {m.number} · Filed {m.filed}
                {m.validity && <span className="block text-[9px] text-[#0284C7]">{m.validity}</span>}
              </span>
              <span className={`f-mono text-[9px] tracking-[0.18em] ${s.cls}`}>
                {s.dot} {m.status.toUpperCase()}
              </span>
            </div>
          );
        })}
      </motion.div>
      )}

      {!q && (
      <motion.p variants={rise} className="max-w-3xl text-[13px] italic leading-relaxed text-[#64748B]" data-testid="tm-note">
        {tm.note}
      </motion.p>
      )}

      {!q && (
      <motion.p
        variants={rise}
        tabIndex={0}
        className="group f-mono cursor-default text-[8px] tracking-[0.3em] text-[#B6C2D1] outline-none"
        data-testid="vault-archive-note"
        title="Recovered fragment — operator eyes only"
      >
        ARCHIVE 1986 · LEGACY SEQUENCE FRAGMENT:{" "}
        <span className="group-hover:hidden group-focus:hidden">██ ██ ██ █ █</span>
        <span className="hidden text-[#0284C7] group-hover:inline group-focus:inline">← → ← → B A</span>
      </motion.p>
      )}

      <AnimatePresence>
        {detail && <VaultDetail record={detail} onClose={closeDetail} />}
      </AnimatePresence>
    </motion.div>
  );
}
